import type {
  AgeGroup,
  CountryCode,
  Gender,
  KindOfResearchStudy,
  LookupGroup,
  RespondentDashboardSort,
  ScreenerResponseSortField,
  ScreenerResponseStatus,
  TaskType,
} from '@/api/core-api';

/**
 * Visibility view over the researcher applicants list. Default (undefined) is
 * the visible roster; `favorite` narrows it to favorites; `hidden` swaps to
 * the hidden rows.
 */
export type ParticipantVisibilityView = 'favorite' | 'hidden';

/**
 * Sorted copy, or `undefined` when empty. The request builders drop empty
 * arrays (`length > 0` gates) and the API treats these as sets, so `[]` vs
 * `undefined` and differing toggle order must not become separate cache
 * entries that refetch already-loaded pages.
 */
function asFilterSet<T extends string | number>(
  values: readonly T[] | undefined,
): T[] | undefined {
  if (!values?.length) return undefined;
  return [...values].toSorted((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

function normalizeProjectFilters(params: {
  statuses?: string[];
  recruitingStatuses?: string[];
  kindsOfResearch?: number[];
  programIds?: string[];
}) {
  return {
    statuses: asFilterSet(params.statuses),
    recruitingStatuses: asFilterSet(params.recruitingStatuses),
    kindsOfResearch: asFilterSet(params.kindsOfResearch),
    programIds: asFilterSet(params.programIds),
  };
}

/**
 * Keys for a project read that can be made at a feature schema version.
 * `byProject` is the prefix every version shares, so invalidating it reaches
 * them all.
 */
function projectReadAtVersionKeys<const Name extends string>(name: Name) {
  return {
    byProject: (projectId: string) => [name, projectId] as const,
    byProjectAtVersion: (projectId: string, featuresSchemaVersion?: number) =>
      [name, projectId, { featuresSchemaVersion }] as const,
  };
}

export const queryKeys = {
  plaidVerificationLinkToken: {
    /** Prefix for every link-token variant — invalidate by this after LinkedIn OAuth returns. */
    all: ['plaid-verification-link-token'] as const,
    byUserId: (userId: string, projectId?: string) =>
      ['plaid-verification-link-token', 'user', userId, projectId] as const,
  },
  session: {
    current: () => ['session', 'current'],
  },
  consent: {
    region: () => ['consent', 'region'] as const,
  },
  profile: {
    byUserId: (userId: string) => ['profile', 'user', userId] as const,
    screenerCycle: (profileId: string) =>
      ['profile', 'screener-cycle', profileId] as const,
    applicationSignals: (userId: string) =>
      ['profile', 'application-signals', userId] as const,
  },
  projects: {
    matchedBase: ['projects', 'matched'] as const,
    matched: (
      profileId: string,
      kindsOfResearchStudy?: Array<KindOfResearchStudy>,
      sort?: RespondentDashboardSort,
      queryText?: string,
      eligibleOnly?: boolean,
    ) =>
      [
        'projects',
        'matched',
        profileId,
        kindsOfResearchStudy?.join(','),
        sort,
        queryText,
        eligibleOnly,
      ] as const,
    /**
     * The three studies offered on the post-screener outcome screens.
     * Deliberately outside `matchedBase` so it never shares, or is invalidated
     * with, the dashboard's infinite list.
     */
    postScreenerRecommendations: (profileId: string) =>
      ['projects', 'post-screener-recommendations', profileId] as const,
    /** Prefix for the recommendations above; dropped on every screener save. */
    postScreenerRecommendationsBase: [
      'projects',
      'post-screener-recommendations',
    ] as const,
    /**
     * The dashboard's Top picks strip: every matched or boosted eligible
     * project. Lives under `matchedBase` on purpose so hiding a project
     * refetches it together with the feed.
     */
    topPicks: (profileId: string) =>
      ['projects', 'matched', 'top-picks', profileId] as const,
    detail: <T extends object>(
      projectId: string,
      params?: T,
      userId?: string,
    ) => ['projects', 'detail', projectId, params, userId] as const,
    detailBase: (projectId: string) =>
      ['projects', 'detail', projectId] as const,
    screener: (projectId: string, userId?: string) =>
      ['projects', 'screener', projectId, userId] as const,
    /**
     * Prefix shared by every screener variant — invalidate by this when the
     * project id isn't in scope (e.g. the LinkedIn OAuth return hook) to
     * refetch outstanding pre-submission steps wherever they're shown.
     */
    screenerBase: ['projects', 'screener'] as const,
    previouslyUsedSearch: (
      projectId: string,
      params: {
        searchText?: string;
        projectType?: 'my' | 'team';
        timeframeDays?: number;
      },
    ) => ['projects', 'previously-used-questions', projectId, params] as const,
    previouslyUsedProjectSearch: (
      projectId: string,
      params: {
        searchText?: string;
        projectType?: 'my' | 'team';
        timeframeDays?: number;
      },
    ) =>
      [
        'projects',
        'previously-used-project-search',
        projectId,
        params,
      ] as const,
  },
  booking: {
    /** A participant's booking state on a project (calendar, slot, gates). */
    byProject: (projectId: string, userId: string) =>
      ['booking', projectId, userId] as const,
    /** Free slots for a window; keyed by the window so weeks cache apart. */
    times: (
      projectId: string,
      userId: string,
      params: { date: string; timezone: string; length?: number },
    ) => ['booking', projectId, userId, 'times', params] as const,
  },
  featureFlags: {
    /** Participant-side kill switch for the dashboard surfacing experiment. */
    dashboardMatchedSurfacing: [
      'feature-flags',
      'dashboard-matched-surfacing',
      'active',
    ] as const,
    settingsV3: (
      userId: string,
      organizationId: string | null,
      teamId: string | null,
    ) =>
      ['feature-flags', 'settings-v3', userId, organizationId, teamId] as const,
  },
  teamBalance: {
    byTeamId: (teamId: string) => ['team-balance', teamId] as const,
  },
  researcherProjects: {
    detail: (projectId: string) =>
      ['researcher-projects', 'detail', projectId] as const,
    pricingMultiplier: (projectId: string) =>
      ['researcher-projects', 'pricing-multiplier', projectId] as const,
    incentiveRecommendation: (projectId: string) =>
      ['researcher-projects', 'incentive-recommendation', projectId] as const,
    interviewContentTranslation: (projectId: string, language: string) =>
      [
        'researcher-projects',
        'interview-content-translation',
        projectId,
        language,
      ] as const,
  },
  teamInbox: {
    unreadCounts: (teamId: string, projectIds: string[]) =>
      ['team-inbox', teamId, 'unread-counts', projectIds] as const,
    /**
     * Prefix shared by every unread-counts variant for a team (the full key
     * also carries the projectIds array). Invalidate by this to refetch all
     * unread-count queries for a team regardless of which project set they hold
     * — e.g. after a realtime `newmessage` event.
     */
    unreadCountsForTeam: (teamId: string) =>
      ['team-inbox', teamId, 'unread-counts'] as const,
  },
  teamProjects: {
    all: (teamId: string) => ['team-projects', teamId] as const,
    list: (
      teamId: string,
      params: {
        page: number;
        pageSize?: number;
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
        statuses?: string[];
        closed?: boolean;
        name?: string;
        userId?: string;
        recruitingStatuses?: string[];
        kindsOfResearch?: number[];
        programIds?: string[];
      },
    ) =>
      [
        'team-projects',
        teamId,
        'list',
        { ...params, ...normalizeProjectFilters(params) },
      ] as const,
    /**
     * Infinite (scroll-to-load) projects list, keyed by filter/sort state
     * only — pages live inside the one cache entry. Under the `all` prefix so
     * project mutations refresh every loaded page.
     */
    infinite: (
      teamId: string,
      params: {
        pageSize?: number;
        sortBy?: string;
        sortDir?: 'asc' | 'desc';
        statuses?: string[];
        closed?: boolean;
        name?: string;
        userId?: string;
        recruitingStatuses?: string[];
        kindsOfResearch?: number[];
        programIds?: string[];
      },
    ) =>
      [
        'team-projects',
        teamId,
        'infinite',
        { ...params, ...normalizeProjectFilters(params) },
      ] as const,
  },
  teamParticipants: {
    /** Prefix for every team-participants query — invalidate to refetch all. */
    all: (teamId: string) => ['team-participants', teamId] as const,
    list: (
      teamId: string,
      params: {
        page: number;
        name?: string;
        jobTitle?: string;
        source?: string;
        lastPaidAt?: string;
        projectIds?: string[];
        sortBy?: string;
      },
    ) => ['team-participants', teamId, 'list', params] as const,
    detail: (teamId: string, profileId: string) =>
      ['team-participants', teamId, 'detail', profileId] as const,
  },
  programs: {
    /** Prefix for every programs query on a team — invalidate to refetch all. */
    all: (teamId: string) => ['programs', teamId] as const,
    list: (teamId: string) => ['programs', teamId, 'list'] as const,
    detail: (teamId: string, programId: string) =>
      ['programs', teamId, 'detail', programId] as const,
    participants: (teamId: string, programId: string) =>
      ['programs', teamId, 'participants', programId] as const,
    /**
     * Reverse lookup for a project's program. Keyed under the same `programs`
     * prefix so adding a project to a program invalidates the breadcrumb too.
     */
    byProject: (teamId: string, projectId: string) =>
      ['programs', teamId, 'by-project', projectId] as const,
  },
  aiChatThread: {
    byProject: (projectId: string) => ['ai-chat-thread', projectId] as const,
  },
  insightsChat: {
    threads: (projectId: string) =>
      ['insights-chat', projectId, 'threads'] as const,
    /** Everything cached for a thread: itself at every version, and its charts. */
    threadScope: (projectId: string, threadId: string) =>
      ['insights-chat', projectId, 'thread', threadId] as const,
    /** A thread at every version it was read at. */
    threadVersions: (projectId: string, threadId: string) =>
      ['insights-chat', projectId, 'thread', threadId, 'version'] as const,
    thread: (
      projectId: string,
      threadId: string,
      featuresSchemaVersion: number | undefined,
    ) =>
      [
        'insights-chat',
        projectId,
        'thread',
        threadId,
        'version',
        featuresSchemaVersion ?? null,
      ] as const,
    chart: (
      projectId: string,
      threadId: string,
      chartId: string,
      breakdown: string | null,
      featuresSchemaVersion: number | undefined,
    ) =>
      [
        'insights-chat',
        projectId,
        'thread',
        threadId,
        'chart',
        chartId,
        breakdown,
        featuresSchemaVersion ?? null,
      ] as const,
  },
  paidCount: {
    byUserId: (userId: string) => ['paid-count', 'user', userId] as const,
  },
  totalEarned: {
    byUserId: (userId: string) => ['total-earned', 'user', userId] as const,
  },
  activeSurveys: {
    byUserId: (userId: string) => ['active-surveys', 'user', userId] as const,
  },
  /** Pending (not yet applied) invitations, participant side. */
  participantInvitations: {
    /** Prefix for every user — for invalidation where no user id is at hand. */
    all: () => ['participant-invitations'] as const,
    byUserId: (userId: string) =>
      ['participant-invitations', 'user', userId] as const,
  },
  /** Pending (not yet applied) invitations on a project, researcher side. */
  projectInvitations: {
    byProjectId: (projectId: string) =>
      ['project-invitations', 'project', projectId] as const,
  },
  appliedSurveys: {
    byUserId: (userId: string) => ['applied-surveys', 'user', userId] as const,
  },
  pastSurveys: {
    byUserId: (userId: string) => ['past-surveys', 'user', userId] as const,
  },
  lookups: {
    targeting: () => ['lookups', 'targeting'] as const,
    top: (kind: string) => ['lookups', kind, 'top'] as const,
    search: (kind: string, query: string) =>
      ['lookups', kind, 'search', query] as const,
    byGroup: (group: LookupGroup) => ['lookups', group] as const,
    quotaAttributes: () => ['lookups', 'quota-attributes'] as const,
    /** One job title's name, by id (for labels on saved quota rules). */
    jobTitle: (id: string) => ['lookups', 'job-title', id] as const,
  },
  quota: {
    /** Every quota read for the project; invalidate it to refresh V1 and V2 alike. */
    all: (projectId: string) => ['quota', 'project', projectId] as const,
    /** The V1 quota (`useQuotaQuery`). */
    v1: (projectId: string) => ['quota', 'project', projectId, 'v1'] as const,
    /** `GET …/quotas`: the Quotas V2 configuration with progress, or the V1 quota. */
    config: (projectId: string) =>
      ['quota', 'project', projectId, 'config'] as const,
  },
  recruitmentStats: {
    byProjectId: (projectId: string) =>
      ['recruitment-stats', 'project', projectId] as const,
  },
  pricing: {
    requirements: (teamId: string, projectId: string) =>
      ['pricing', 'requirements', teamId, projectId] as const,
    rateCard: (teamId: string) => ['pricing', 'rateCard', teamId] as const,
    projectReservations: (teamId: string, projectId: string) =>
      ['pricing', 'projectReservations', teamId, projectId] as const,
    teamPlanSummary: (teamId: string) =>
      ['pricing', 'teamPlanSummary', teamId] as const,
  },
  participantReferrals: {
    byReferrer: (
      userId: string,
      query?: string,
      includeLifetimeBonusReceived?: boolean,
      hideCompleted?: boolean,
    ) =>
      [
        'participant-referrals',
        'referrer',
        userId,
        query,
        includeLifetimeBonusReceived,
        hideCompleted,
      ] as const,
  },
  creditCards: {
    byTeamId: (teamId: string) => ['credit-cards', teamId] as const,
  },
  braintree: {
    clientToken: () => ['braintree', 'clientToken'] as const,
  },
  interview: {
    byProjectId: (projectId: string) =>
      ['interview', 'project', projectId] as const,
    questionMedia: (projectId: string, questionUid: string) =>
      [
        'interview',
        'project',
        projectId,
        'question-media',
        questionUid,
      ] as const,
  },
  feedback: {
    bySubject: (
      context: string,
      subject:
        | { projectId: string; interviewResponseId?: string }
        | { projectId?: string; interviewResponseId: string },
    ) =>
      subject.interviewResponseId
        ? ([
            'feedback',
            context,
            'interview',
            subject.interviewResponseId,
          ] as const)
        : (['feedback', context, 'project', subject.projectId] as const),
  },
  researcherApplicants: {
    byProjectId: (projectId: string) =>
      ['researcher-applicants', 'project', projectId] as const,
    byProjectIdPage: (
      projectId: string,
      page: number,
      statuses?: readonly ScreenerResponseStatus[],
      sortBy?: ScreenerResponseSortField,
      q?: string,
      view?: ParticipantVisibilityView,
      qualified?: boolean,
      rejected?: boolean,
      genders?: readonly Gender[],
      ageGroups?: readonly AgeGroup[],
      countries?: readonly CountryCode[],
    ) =>
      [
        'researcher-applicants',
        'project',
        projectId,
        {
          page,
          statuses: statuses ? [...statuses].toSorted() : undefined,
          sortBy: sortBy ?? null,
          q: q || undefined,
          view: view ?? null,
          qualified: qualified ?? null,
          rejected: rejected ?? null,
          genders: genders ? [...genders].toSorted() : undefined,
          ageGroups: ageGroups ? [...ageGroups].toSorted() : undefined,
          countries: countries ? [...countries].toSorted() : undefined,
        },
      ] as const,
    byProjectAndUserId: (projectId: string, participantUserId: string) =>
      [
        'researcher-applicants',
        'project',
        projectId,
        { participantUserId },
      ] as const,
    /** Infinite (paged) applicants list, filtered by search query. */
    infiniteByProject: (projectId: string, q?: string) =>
      [
        'researcher-applicants',
        'project',
        projectId,
        'infinite',
        { q: q || undefined },
      ] as const,
    /**
     * Infinite applicants list for the Participants tab, keyed by the full
     * filter/sort state. Under the `byProjectId` prefix so participant action
     * invalidations refresh all loaded pages.
     */
    infiniteTableByProject: (
      projectId: string,
      filters: {
        statuses?: readonly ScreenerResponseStatus[];
        sortBy?: ScreenerResponseSortField;
        q?: string;
        view?: ParticipantVisibilityView;
        qualified?: boolean;
        rejected?: boolean;
        genders?: readonly Gender[];
        ageGroups?: readonly AgeGroup[];
        countries?: readonly CountryCode[];
        quotaSegments?: readonly string[];
      },
    ) =>
      [
        'researcher-applicants',
        'project',
        projectId,
        'infinite-table',
        {
          // Empty arrays are dropped from the request (`length > 0` gates),
          // so they must collapse to `undefined` here too — otherwise an
          // empty-filter key and a no-filter key point at the same request
          // yet occupy different cache entries and refetch loaded pages.
          statuses: filters.statuses?.length
            ? [...filters.statuses].toSorted()
            : undefined,
          sortBy: filters.sortBy ?? null,
          q: filters.q || undefined,
          view: filters.view ?? null,
          qualified: filters.qualified ?? null,
          rejected: filters.rejected ?? null,
          genders: filters.genders?.length
            ? [...filters.genders].toSorted()
            : undefined,
          ageGroups: filters.ageGroups?.length
            ? [...filters.ageGroups].toSorted()
            : undefined,
          countries: filters.countries?.length
            ? [...filters.countries].toSorted()
            : undefined,
          quotaSegments: filters.quotaSegments?.length
            ? [...filters.quotaSegments].toSorted()
            : undefined,
        },
      ] as const,
    /**
     * Completed online-task responses (Completes tab). Nested under the
     * applicants family so participant action invalidation (`byProjectId`
     * prefix) refreshes it too.
     */
    completes: (
      projectId: string,
      page: number,
      taskType: TaskType,
      userId?: string,
    ) =>
      [
        'researcher-applicants',
        'project',
        projectId,
        'completes',
        { page, taskType, userId: userId ?? null },
      ] as const,
  },
  researcherBookings: {
    byProjectId: (projectId: string) =>
      ['researcher-bookings', 'project', projectId] as const,
  },
  researcherPayments: {
    overview: (projectId: string) =>
      ['researcher-payments', 'overview', projectId] as const,
    /**
     * Prefix shared by every "participants to pay" variant (search /
     * payoutCountFilter / page). Invalidate by this to refetch all of them —
     * keep the segments in lockstep with `participantsToPay` below.
     */
    participantsToPayPrefix: (projectId: string) =>
      ['researcher-payments', 'participants-to-pay', projectId] as const,
    participantsToPay: (
      projectId: string,
      params?: {
        search?: string;
        payoutCountFilter?: string;
        page?: number;
      },
    ) =>
      [
        'researcher-payments',
        'participants-to-pay',
        projectId,
        params?.search,
        params?.payoutCountFilter,
        params?.page,
      ] as const,
    /** Prefix shared by every "past payments" variant — invalidate to refetch all. */
    pastPaymentsPrefix: (projectId: string) =>
      ['researcher-payments', 'past-payments', projectId] as const,
    pastPayments: (
      projectId: string,
      params?: { search?: string; page?: number },
    ) =>
      [
        'researcher-payments',
        'past-payments',
        projectId,
        params?.search,
        params?.page,
      ] as const,
    /**
     * Scroll-loaded past payments for one search query. Deliberately nested
     * under `pastPaymentsPrefix` so a payout invalidates every loaded chunk
     * along with the paged variants.
     */
    pastPaymentsInfinite: (projectId: string, params?: { search?: string }) =>
      [
        'researcher-payments',
        'past-payments',
        projectId,
        'infinite',
        params?.search,
      ] as const,
    legacyPayments: (projectId: string, params?: { page?: number }) =>
      [
        'researcher-payments',
        'legacy-payments',
        projectId,
        params?.page,
      ] as const,
    summaryPreview: (projectId: string, selectionKey: string) =>
      [
        'researcher-payments',
        'summary-preview',
        projectId,
        selectionKey,
      ] as const,
  },
  researcherCalendars: {
    byProjectId: (projectId: string) =>
      ['researcher-calendars', 'project', projectId] as const,
  },
  zoomConnection: {
    byUserId: (userId: string) => ['zoom-connection', 'user', userId] as const,
  },
  researcherAccountSettings: {
    byUserId: (userId: string) =>
      ['researcher-account-settings', 'user', userId] as const,
  },
  calendarConnection: {
    byUserId: (userId: string) =>
      ['calendar-connection', 'user', userId] as const,
  },
  calendarDefaults: {
    byUserId: (userId: string) =>
      ['calendar-defaults', 'user', userId] as const,
  },
  workspaceSettings: {
    all: () => ['workspace-settings'] as const,
    context: (userId: string, organizationId: string, teamId: string) =>
      [
        'workspace-settings',
        'context',
        userId,
        organizationId,
        teamId,
      ] as const,
    organization: (organizationId: string) =>
      ['workspace-settings', 'organization', organizationId] as const,
    team: (teamId: string) => ['workspace-settings', 'team', teamId] as const,
    switcher: (userId: string) =>
      ['workspace-settings', 'switcher', userId] as const,
  },
  researcherTeamsOverview: {
    byUserId: (userId: string) =>
      ['researcher-teams-overview', 'user', userId] as const,
  },
  researcherInterviewResponses: {
    byProject: (projectId: string) =>
      ['researcher-interview-responses', projectId, 'all'] as const,
    byProjectPage: (
      projectId: string,
      page: number,
      extractedOnly: boolean,
      featuresSchemaVersion?: number,
    ) =>
      [
        'researcher-interview-responses',
        projectId,
        'all',
        { page, extractedOnly, featuresSchemaVersion },
      ] as const,
    byProjectAndUser: (projectId: string, participantUserId: string) =>
      ['researcher-interview-responses', projectId, participantUserId] as const,
  },
  researcherFeaturesSchemaVersions: {
    byProject: (projectId: string) =>
      ['researcher-features-schema-versions', projectId] as const,
  },
  researcherQuestionSummaries: projectReadAtVersionKeys(
    'researcher-question-summaries',
  ),
  insightsReport: projectReadAtVersionKeys('insights-report'),
  projectInsightsStatus: projectReadAtVersionKeys('project-insights-status'),
  insightsReportChart: {
    evaluate: (
      projectId: string,
      featuresSchemaVersion: number | undefined,
      chartId: string,
      breakdown: string | null,
    ) =>
      [
        'insights-report',
        projectId,
        'chart',
        { featuresSchemaVersion, chartId, breakdown },
      ] as const,
  },
  insightsReportStatements: {
    byProject: (projectId: string) =>
      ['insights-report-statements', projectId] as const,
    positions: (
      projectId: string,
      featuresSchemaVersion: number | undefined,
      statementIds: readonly number[],
    ) =>
      [
        'insights-report-statements',
        projectId,
        'positions',
        { featuresSchemaVersion, statementIds },
      ] as const,
    search: (
      projectId: string,
      featuresSchemaVersion: number | undefined,
      q: string,
    ) =>
      [
        'insights-report-statements',
        projectId,
        'search',
        { featuresSchemaVersion, q },
      ] as const,
  },
  researcherConceptReport: projectReadAtVersionKeys(
    'researcher-concept-report',
  ),
  researcherQuoteClips: projectReadAtVersionKeys('researcher-quote-clips'),
  audienceEstimate: (paramsKey: string) =>
    ['audience-estimate', paramsKey] as const,
  surveyMonkeySurveys: {
    byTeamId: (teamId: string) => ['surveymonkey-surveys', teamId] as const,
  },
  organizations: {
    detail: (organizationId: string) =>
      ['organizations', organizationId, 'detail'] as const,
    featureFlag: (organizationId: Maybe<string>, flag: string) =>
      ['organizations', organizationId, 'feature-flag', flag] as const,
    activeFeatureFlags: (organizationId: Maybe<string>) =>
      ['organizations', organizationId, 'active-feature-flags'] as const,
  },
  screenerFiles: {
    signedUrlByBucketKey: (bucketKey: string) =>
      ['screener-files', 'signed-url', bucketKey] as const,
  },
  interviewMedia: {
    signedUrlByBucketKey: (bucketKey: string) =>
      ['interview-media', 'signed-url', bucketKey] as const,
    captionsByBucketKey: (bucketKey: string, language: string) =>
      ['interview-media', 'captions', bucketKey, language] as const,
  },
  /** Media (images) attached to interview questions by the researcher. */
  projectMedia: {
    signedUrlByBucketKey: (bucketKey: string) =>
      ['project-media', 'signed-url', bucketKey] as const,
  },
  teamMembers: {
    list: (teamId: string) => ['team-members', teamId] as const,
  },
  teams: {
    detail: (organizationId: string, teamId: string) =>
      ['teams', organizationId, teamId, 'detail'] as const,
  },
  conversations: {
    byProject: (projectId: string) =>
      ['conversations', 'project', projectId] as const,
    /**
     * Per-search inbox key, nested under `byProject` so invalidating the
     * project prefix refreshes every search variant. `undefined` search is the
     * unfiltered inbox.
     */
    byProjectSearch: (projectId: string, search?: string) =>
      [
        'conversations',
        'project',
        projectId,
        { search: search ?? '' },
      ] as const,
    /**
     * Team-wide researcher inbox (every conversation on the team). Prefix key:
     * invalidate it to refresh every search variant at once.
     */
    byTeam: (teamId: string) => ['conversations', 'team', teamId] as const,
    /**
     * Per-search team inbox key, nested under `byTeam` so invalidating the
     * team prefix refreshes every search variant. `undefined` search is the
     * unfiltered inbox.
     */
    byTeamSearch: (teamId: string, search?: string) =>
      ['conversations', 'team', teamId, { search: search ?? '' }] as const,
    /** Single conversation fetched by uid (thread-pane fallback). */
    byUid: (conversationUid: string) =>
      ['conversations', 'uid', conversationUid] as const,
    /**
     * The signed-in participant's cross-project inbox. Prefix key: invalidate
     * it to refresh every search variant at once.
     */
    mine: () => ['conversations', 'mine'] as const,
    /**
     * Per-search participant inbox key, nested under `mine` so invalidating the
     * `mine` prefix refreshes every search variant. `undefined` search is the
     * unfiltered inbox.
     */
    mineSearch: (search?: string) =>
      ['conversations', 'mine', { search: search ?? '' }] as const,
  },
  conversationMessages: {
    byConversation: (conversationUid: string) =>
      ['conversation-messages', conversationUid] as const,
  },
  participantInbox: {
    /** The signed-in participant's own inbox summary (nav unread badge). */
    mine: ['participant-inbox'] as const,
  },
  respondentMessages: {
    /** The signed-in participant's one-way pinned "Respondent" thread. */
    mine: ['respondent-messages'] as const,
  },
  participantMessagingProjects: {
    /**
     * Prefix shared by every user's variant — invalidate by this to refetch
     * the compose picker regardless of user (there's only one signed-in user).
     */
    base: ['participant-messaging-projects'] as const,
    byUserId: (userId: string) =>
      ['participant-messaging-projects', userId] as const,
  },
};
